import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { fetchProducts, fetchCategories, type Product, type Category } from '@/services/api';

const PAGE_SIZE = 8;

export default function ProductsPage() {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState<Product[]>([]);
  const [categoryList, setCategoryList] = useState<Category[]>([]);
  const [categories, setCategories] = useState<string[]>(['All']);
  const [activeCategory, setActiveCategory] = useState(() => searchParams.get('category') || 'All');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // 加载分类
  useEffect(() => {
    fetchCategories()
      .then((cats: Category[]) => {
        setCategoryList(cats);
        const names = ['All', ...cats.map((c) => c.name)];
        setCategories(names);
      })
      .catch(() => {});
  }, []);

  // 加载产品
  useEffect(() => {
    setLoading(true);
    fetchProducts(currentPage, PAGE_SIZE, activeCategory)
      .then((res) => {
        setProducts(res.items);
        setTotalPages(res.total_pages);
        setTotal(res.total);
      })
      .catch(() => {
        setProducts([]);
        setTotalPages(1);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [currentPage, activeCategory]);

  useEffect(() => {
    document.title = 'Premium Tool Bags & Organizer Bags | YUNXIN OUTDOOR';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Explore 200+ premium tool bags and organizer bags from Yunxin Outdoor. Heavy-duty, professional-grade bags for tradespeople worldwide. OEM/ODM available.');
  }, []);

  const handleCategoryChange = (cat: string) => {
    setActiveCategory(cat);
    setCurrentPage(1);
  };

  const openProduct = (product: Product) => {
    setSelectedProduct(product);
    setActiveImageIndex(0);
  };

  const closeProduct = () => {
    setSelectedProduct(null);
    setActiveImageIndex(0);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-stone-900">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <span className="text-amber-400 text-xs font-semibold tracking-widest uppercase mb-4 block">Our Products</span>
          <h1 className="text-5xl md:text-6xl font-bold text-white leading-tight mb-4">
            Premium Bags<br />Collection
          </h1>
          <p className="text-stone-400 text-base max-w-xl leading-relaxed">
            Explore our comprehensive range of tool bags and organizer bags, engineered for professionals who demand the best.
          </p>
        </div>
      </section>

      {/* Category Filter Tabs */}
      <section className="sticky top-20 z-30 bg-white border-b border-stone-100 py-4">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-4">
            <span className="text-stone-400 text-xs font-medium tracking-wider uppercase shrink-0">Filter:</span>
            <div className="flex gap-2 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => handleCategoryChange(cat)}
                  className={`px-5 py-2 rounded-full text-xs font-semibold tracking-wider uppercase transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat
                      ? 'bg-stone-900 text-white'
                      : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
          {activeCategory !== 'All' && (
            <div className="mt-2 flex items-center gap-2">
              <span className="text-stone-500 text-sm">Showing products in</span>
              <span className="bg-amber-100 text-amber-700 text-xs font-semibold px-3 py-1 rounded-full">{activeCategory}</span>
              <button
                onClick={() => handleCategoryChange('All')}
                className="text-stone-400 hover:text-stone-600 text-xs underline cursor-pointer"
              >
                Clear filter
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          {loading ? (
            <div className="text-center py-20">
              <p className="text-stone-400">Loading products...</p>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20">
              <i className="ri-box-3-line text-stone-300 text-5xl mb-4 block" />
              <p className="text-stone-400 text-lg">No products found.</p>
              {activeCategory !== 'All' && (
                <button
                  onClick={() => handleCategoryChange('All')}
                  className="mt-4 text-amber-600 hover:text-amber-700 text-sm font-medium underline cursor-pointer"
                >
                  View all products
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-8">
                <p className="text-stone-500 text-sm">{total} product{total !== 1 ? 's' : ''}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" data-product-shop>
                {products.map((product) => {
                  const img = product.images?.[0]?.url || '';
                  return (
                    <div
                      key={product.id}
                      className="group bg-white border border-stone-100 rounded-2xl overflow-hidden cursor-pointer hover:border-stone-300 transition-all duration-300"
                      onClick={() => openProduct(product)}
                    >
                      <div className="relative w-full aspect-square bg-stone-50 overflow-hidden">
                        {img && (
                          <img
                            src={img}
                            alt={product.name}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                          />
                        )}
                        {product.tag && (
                          <span className="absolute top-3 left-3 bg-stone-900 text-white text-xs font-medium px-3 py-1 rounded-full">
                            {product.tag}
                          </span>
                        )}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300 flex items-center justify-center">
                          <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white text-stone-900 text-xs font-semibold tracking-widest uppercase px-5 py-2.5 rounded-full">
                            View Details
                          </span>
                        </div>
                      </div>
                      <div className="p-5">
                        <span className="text-amber-600 text-xs font-medium tracking-wider uppercase">{product.category_name}</span>
                        <h3 className="text-stone-900 font-semibold text-base mt-1 mb-2">{product.name}</h3>
                        <p className="text-stone-500 text-xs leading-relaxed line-clamp-2">{product.description || product.material}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="w-10 h-10 flex items-center justify-center rounded-full border border-stone-200 text-stone-600 hover:border-stone-400 hover:text-stone-900 disabled:opacity-30 disabled:cursor-not-allowed transition-all cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line" />
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-10 h-10 flex items-center justify-center rounded-full text-sm font-semibold transition-all cursor-pointer ${
                        currentPage === page
                          ? 'bg-stone-900 text-white'
                          : 'border border-stone-200 text-stone-600 hover:border-stone-400 hover:text-stone-900'
                      }`}
                    >
                      {page}
                    </button>
                  ))}

                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="w-10 h-10 flex items-center justify-center rounded-full border border-stone-200 text-stone-600 hover:border-stone-400 hover:text-stone-900 disabled:opacity-30 disabled:cursor-not-allowed transition-all cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Inquiry CTA */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <h2 className="text-3xl font-bold text-stone-900 mb-4">Looking for Custom Solutions?</h2>
          <p className="text-stone-500 text-base leading-relaxed mb-8">
            We offer OEM/ODM services for businesses looking to create custom-branded bags. Contact our team to discuss your specific requirements.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 bg-stone-900 hover:bg-stone-700 text-white px-8 py-4 rounded-full text-sm font-semibold tracking-widest uppercase transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Request a Quote
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line" />
            </div>
          </a>
        </div>
      </section>

      {/* Product Detail Modal */}
      {selectedProduct && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={closeProduct}
        >
          <div
            className="bg-white rounded-2xl max-w-5xl w-full overflow-hidden max-h-[95vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={closeProduct}
              className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-white/90 hover:bg-white shadow-lg transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-stone-600 text-xl" />
            </button>

            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Left: Image Gallery */}
              <div className="bg-stone-100 p-6 lg:p-8">
                {/* Main Image */}
                <div className="relative w-full aspect-square rounded-xl overflow-hidden bg-white mb-4">
                  {selectedProduct.images?.[activeImageIndex]?.url && (
                    <img
                      src={selectedProduct.images[activeImageIndex].url}
                      alt={selectedProduct.name}
                      className="w-full h-full object-cover object-center"
                    />
                  )}
                  {/* Image Counter */}
                  <div className="absolute bottom-4 right-4 bg-black/60 text-white text-xs px-3 py-1 rounded-full">
                    {activeImageIndex + 1} / {selectedProduct.images?.length || 1}
                  </div>
                </div>

                {/* Thumbnail Gallery */}
                {selectedProduct.images && selectedProduct.images.length > 1 && (
                  <div className="flex gap-3 overflow-x-auto pb-2">
                    {selectedProduct.images.map((img, idx) => (
                      <button
                        key={img.id || idx}
                        onClick={() => setActiveImageIndex(idx)}
                        className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                          idx === activeImageIndex
                            ? 'border-amber-500 ring-2 ring-amber-200'
                            : 'border-transparent hover:border-stone-300'
                        }`}
                      >
                        <img
                          src={img.url}
                          alt={`${selectedProduct.name} view ${idx + 1}`}
                          className="w-full h-full object-cover object-center"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Right: Product Info */}
              <div className="p-6 lg:p-8 flex flex-col">
                {/* Header */}
                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-amber-600 text-xs font-semibold tracking-wider uppercase">
                      {selectedProduct.category_name}
                    </span>
                    {selectedProduct.tag && (
                      <span className="bg-stone-900 text-white text-xs font-medium px-3 py-1 rounded-full">
                        {selectedProduct.tag}
                      </span>
                    )}
                  </div>
                  <h2 className="text-3xl font-bold text-stone-900 mb-3">{selectedProduct.name}</h2>
                  <p className="text-stone-500 text-sm leading-relaxed">{selectedProduct.description}</p>
                </div>

                {/* Material Section */}
                {selectedProduct.material && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-stone-900 uppercase tracking-wider mb-2 flex items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-t-shirt-line text-amber-600" />
                      </div>
                      Main Materials
                    </h3>
                    <p className="text-stone-600 text-sm leading-relaxed bg-stone-50 p-3 rounded-lg">
                      {selectedProduct.material}
                    </p>
                  </div>
                )}

                {/* Specifications */}
                {selectedProduct.specifications && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-stone-900 uppercase tracking-wider mb-2 flex items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-ruler-line text-amber-600" />
                      </div>
                      Product Specifications
                    </h3>
                    <p className="text-stone-600 text-sm leading-relaxed bg-stone-50 p-3 rounded-lg whitespace-pre-line">
                      {selectedProduct.specifications}
                    </p>
                  </div>
                )}

                {/* Notes Section */}
                {selectedProduct.notes && (
                  <div className="mb-8">
                    <h3 className="text-sm font-semibold text-stone-900 uppercase tracking-wider mb-2 flex items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-information-line text-amber-600" />
                      </div>
                      Notes
                    </h3>
                    <p className="text-stone-600 text-sm leading-relaxed italic bg-amber-50/50 border-l-3 border-amber-400 p-3 rounded-r-lg">
                      {selectedProduct.notes}
                    </p>
                  </div>
                )}

                {/* CTA Buttons */}
                <div className="mt-auto space-y-3">
                  <a
                    href="/contact"
                    className="w-full inline-flex items-center justify-center gap-2 bg-stone-900 hover:bg-stone-700 text-white px-6 py-4 rounded-full text-sm font-semibold tracking-widest uppercase transition-all duration-300 whitespace-nowrap cursor-pointer"
                    onClick={closeProduct}
                  >
                    Send Inquiry
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-mail-send-line" />
                    </div>
                  </a>
                  <button
                    onClick={closeProduct}
                    className="w-full inline-flex items-center justify-center gap-2 border border-stone-300 hover:border-stone-400 text-stone-600 hover:text-stone-900 px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    Continue Browsing
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
